import React from "react";
import { canBeProposedAsParticipant } from "../lib/participant-eligibility";
import { SupportChat } from "./SupportChat";
import { useInteractiveGuide, GuideContext } from "./guide/InteractiveGuide";
import { walkthroughScope } from "./guide/walkthroughs";
import { HelpCircle, BookOpen, ShieldCheck } from "lucide-react";
import { publicLegalPdfUrl, useCurrentLegalDocuments } from "../hooks/useCurrentLegalDocuments";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";
import {
  LayoutDashboard,
  Trophy,
  LogOut,
  Menu,
  X,
  History,
  User,
  Database,
  Phone,
  ChevronRight,
  ChevronDown,
  Flame,
  Zap,
  Target,
  Calendar,
  Clock,
  Crosshair,
  BarChart3,
  Award,
  Building2,
  Package,
  UserCircle,
  Settings,
  Radio,
  Dumbbell,
  Star,
  TrendingUp,
  Gift,
  Megaphone,
  PenTool,
  Settings2,
  Play,
} from "lucide-react";
import type { User as UserType, PageType, ActiveChallengeInfo } from "../types";
import { encadrantRoleFor, ENCADRANT_ROLE_META, type EncadrantBloc, canBuyLicences, ouvrirAchatDePack } from "../lib/encadrant";
import { BizCoachLogo } from "./BizCoachLogo";
import { useTranslation } from "react-i18next";
import { changeLanguage } from "../i18n";
import { pickScoringLabel } from "../lib/scoring-i18n";
import { authApi, challengesApi, pseudoApi } from "../lib/api";
import { ChallengeBriefPage } from "./ChallengeBriefPage";
import { PseudoModal } from "./PseudoModal";
import { NotificationBell } from "./NotificationBell";
import { SessionQuotaIndicator } from "./SessionQuotaIndicator";
import { StreakBadge } from "./StreakBadge";
import { BizCoinsBadge } from "./BizCoinsBadge";
import { TrialBanner } from "./TrialBanner";

/**
 * Bandeau "Mode test" affiché quand un ADM utilise l'impersonation rapide via
 * sessionStorage. Cf. src/lib/api.ts (IMPERSONATE_KEY) et src/api/auth.ts (handleMe).
 * À remplacer par une vraie impersonation propre dans un chantier dédié.
 */
function ImpersonationBanner() {
  const [active, setActive] = React.useState<boolean>(() =>
    typeof window !== "undefined" ? !!sessionStorage.getItem("bizcoach_impersonate_user_id") : false
  );
  if (!active) return null;
  return (
    <div className="bg-orange-500 text-white px-4 py-2 text-sm flex items-center justify-between gap-3 sticky top-0 z-[60]">
      <span className="flex-1">
        👁️ <strong>Mode test</strong> — vous voyez l'application comme un autre utilisateur. Les actions effectuées sont réelles, à utiliser avec précaution.
      </span>
      <button
        onClick={() => {
          sessionStorage.removeItem("bizcoach_impersonate_user_id");
          window.location.reload();
        }}
        className="bg-white text-orange-600 px-3 py-1 rounded text-xs font-semibold hover:bg-orange-50 shrink-0"
      >
        Quitter le mode test
      </button>
    </div>
  );
}

interface ActiveTrainingInfo {
  agentName: string;
  agentIcon?: string;
  agentImage?: string;
  agentColor?: string;
  onReturn: () => void;
}

interface LayoutProps {
  user: UserType;
  currentPage: PageType;
  onNavigate: (page: PageType, target?: { tab?: string }) => void;
  onLogout: () => void;
  activeTraining?: ActiveTrainingInfo;
  activeChallenges?: ActiveChallengeInfo[];
  onNavigateToChallenge?: (id: string) => void;
  onChallengeJoined?: () => void;
  encadrantBloc?: EncadrantBloc;
  onSetEncadrantBloc?: (bloc: EncadrantBloc) => void;
  children: React.ReactNode;
}

export function Layout({ user, currentPage, onNavigate, onLogout, activeTraining, activeChallenges, onNavigateToChallenge, onChallengeJoined, encadrantBloc = "piloter", onSetEncadrantBloc, children }: LayoutProps) {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [challengeBannerDismissed, setChalleneBannerDismissed] = React.useState(false);
  const [joiningId, setJoiningId] = React.useState<string | null>(null);
  const [joinConfirmChallenge, setJoinConfirmChallenge] = React.useState<ActiveChallengeInfo | null>(null);
  const [joinTeams, setJoinTeams] = React.useState<Array<{ id: string; name: string; color?: string; members: Array<{ firstName: string; lastName: string }> }>>([]);
  const [joinSelectedTeamId, setJoinSelectedTeamId] = React.useState<string | null>(null);
  const [loadingTeams, setLoadingTeams] = React.useState(false);
  const [interBriefChallengeId, setInterBriefChallengeId] = React.useState<string | null>(null);
  const [pseudoModalOpen, setPseudoModalOpen] = React.useState(false);
  const [pendingJoinChallengeId, setPendingJoinChallengeId] = React.useState<string | null>(null);

  const handleLanguageToggle = () => {
    const newLang = i18n.language === "fr" ? "en" : "fr";
    changeLanguage(newLang);
    authApi.updateProfile({ language: newLang }).catch(() => {});
  };

  // When join popup opens, fetch full challenge details (teams + inter data)
  const [joinChallengeDetails, setJoinChallengeDetails] = React.useState<any>(null);

  React.useEffect(() => {
    if (!joinConfirmChallenge) {
      setJoinTeams([]);
      setJoinSelectedTeamId(null);
      setLoadingTeams(false);
      setJoinChallengeDetails(null);
      return;
    }
    setLoadingTeams(true);
    setJoinSelectedTeamId(null);
    challengesApi.getChallenge(joinConfirmChallenge.id)
      .then((ch: any) => {
        // Teams (participant_choice)
        if (joinConfirmChallenge.teamAssignmentMode === "participant_choice") {
          const teams = (ch.teams || []).map((team: any) => ({
            id: team.id, name: team.name, color: team.color,
            members: (ch.participants || [])
              .filter((p: any) => p.teamId === team.id)
              .map((p: any) => ({ firstName: p.userFirstName || "", lastName: p.userLastName || "" })),
          }));
          setJoinTeams(teams);
        }
        // Store full details for inter brief
        setJoinChallengeDetails(ch);
      })
      .catch(() => { setJoinTeams([]); setJoinChallengeDetails(null); })
      .finally(() => setLoadingTeams(false));
  }, [joinConfirmChallenge?.id]);

  const isAdmin = user.profileType === "ADM" || user.profileType === "COO";
  // Décision du 30/09/2026 : le RH et le GES ne se voient plus proposer de rejoindre un challenge
  // (règle partagée avec le serveur, qui filtre déjà la liste du bandeau).
  const canJoinChallenges = canBeProposedAsParticipant(user.profileType);
  // Page courante exposée via ref pour rester à jour dans les closures async du guide.
  const currentPageRef = React.useRef(currentPage);
  currentPageRef.current = currentPage;
  const { runWalkthrough, getWalkthroughsForProfile } = useInteractiveGuide({
    profileType: user.profileType,
    onNavigate,
    getCurrentPage: () => currentPageRef.current,
    t,
  });
  const [helpMenuOpen, setHelpMenuOpen] = React.useState(false);
  const legalDocs = useCurrentLegalDocuments();
  const [tourAutoStarted, setTourAutoStarted] = React.useState(false);

  // Parcours : visible aux gestionnaires (création) ET aux apprenants (suivi).
  const canSeePaths = isAdmin || ["ORG", "MNG", "BNF", "RH"].includes(user.profileType);

  // Tour de découverte auto au 1er login (par utilisateur). On marque "vu" dès le
  // lancement (et non à la fin) pour ne pas le relancer si l'utilisateur le ferme tôt.
  // Le bouton d'aide permet de le rejouer à tout moment.
  React.useEffect(() => {
    if (tourAutoStarted || (user as any).tourCompletedAt) return;
    setTourAutoStarted(true);
    // Tour d'accueil différencié : un manager/organisateur découvre son espace Pilot,
    // un opérationnel découvre l'espace Play (accueil).
    const firstTour = (user.profileType === "MNG" || user.profileType === "ORG") ? "discover-pilot" : "discover-home";
    const tid = setTimeout(() => runWalkthrough(firstTour), 900);
    pseudoApi.markTourComplete().catch(() => {});
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Visites guidées proposées dans le menu d'aide : filtrées par profil PUIS par la
  // vue active (Play → opérationnel, Pilot → encadrant ; « both » = transverse dans
  // les deux). Les profils sans switcher (BNF/RH/GES) voient toute leur liste.
  const walkthroughItems = getWalkthroughsForProfile().filter((w) => {
    if (!encadrantRoleFor(user.profileType)) return true;
    const sc = walkthroughScope(w.id);
    return currentPage === "encadrant" ? sc !== "play" : sc !== "pilot";
  });
  const launchWalkthrough = (id: string) => { setHelpMenuOpen(false); setMobileMenuOpen(false); runWalkthrough(id); };

  // Barre principale : Accueil · Parcours · Challenges · Sessions training ·
  // Sessions live · Dashboard. « Mon compte » et « Admin » passent dans le menu
  // profil (clic sur la photo). « Parcours » (vue apprenant) est visible par tous.
  // Piste B : 4 espaces (+ Mon équipe pour les managers), une couleur par espace.
  // « Deux casquettes » : rôle encadrant (mode Setup) du profil courant — null si pas de Setup.
  const encadrantRole = encadrantRoleFor(user.profileType);

  const navItems = [
    { id: "dashboard" as PageType, label: t("nav.dashboard"), icon: LayoutDashboard, color: "#5A4BBE" },
    { id: "m-entrainer" as PageType, label: t("nav.mEntrainer"), icon: Dumbbell, color: "#2C6C9E" },
    { id: "sessions-live" as PageType, label: t("nav.terrain"), icon: Radio, color: "#C0423F" },
    { id: "my-progress" as PageType, label: t("nav.progres"), icon: TrendingUp, color: "#1E7A3C" },
    // « Mon équipe » retiré de la vue Play pour les profils qui disposent de l'espace
    // Pilot (MNG/ORG/ADM/COO → Piloter). Conservé UNIQUEMENT pour RH, qui voit le
    // reporting mais n'a pas d'espace Pilot — sinon perte d'accès = régression
    // (décision d'intégration dev-fabien du 27/07, à revalider par Valérie).
    ...(user.profileType === "RH" ? [{ id: "reporting" as PageType, label: t("nav.myTeam"), icon: BarChart3, color: "#926B00" }] : []),
  ];

  // « Deux casquettes » : toggle Opérationnel ↔ Encadrant (Lot 0). En mode Encadrant,
  // la barre = les blocs du rôle (Piloter · Animer · Concevoir · Administrer).
  const inEncadrant = currentPage === "encadrant";
  const BLOC_ICON: Record<EncadrantBloc, typeof BarChart3> = {
    piloter: BarChart3, animer: Megaphone, concevoir: PenTool, administrer: Settings2,
    portefeuille: Building2, transverse: Radio,
  };
  const roleMeta = encadrantRole ? ENCADRANT_ROLE_META[encadrantRole] : null;
  const blocItems = roleMeta
    ? roleMeta.blocs.map((b) => ({ id: b, label: t(`encadrant.bloc.${b}`), icon: BLOC_ICON[b], color: roleMeta.color }))
    : [];

  // Menu profil (clic sur la photo) : Mon profil · Paramètres · Admin · Déconnexion.
  const profileMenuItems = [
    { id: "profile" as PageType, label: t("nav.myProfile"), icon: UserCircle },
    { id: "rewards" as PageType, label: t("nav.rewards"), icon: Gift },
    { id: "account" as PageType, label: t("nav.settings"), icon: Settings },
    ...(isAdmin ? [{ id: "admin" as PageType, label: t("nav.admin"), icon: Database }] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Bandeau Mode test (impersonation rapide ADM) */}
      <ImpersonationBanner />
      {/* Bandeau essai gratuit (visible uniquement si l'user est en trial) */}
      <TrialBanner />
      {/* Header */}
      <header
        className="sticky top-0 z-50 w-full border-b backdrop-blur"
        style={{ background: "linear-gradient(180deg, color-mix(in srgb, var(--color-kestio-violet) 7%, var(--card)), var(--card))" }}
      >
        <div className="container mx-auto px-6 flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex shrink-0 items-center">
            <BizCoachLogo />
          </div>

          {/* Desktop Navigation */}
          <nav data-tour={inEncadrant ? "encadrant-nav" : "play-nav"} className="hidden lg:flex items-center gap-1 mx-4 min-w-0">
            {inEncadrant
              ? blocItems.map((item) => (
                <Button
                  key={item.id}
                  data-tour={`bloc-nav-${item.id}`}
                  variant={encadrantBloc === item.id ? "secondary" : "ghost"}
                  onClick={() => onSetEncadrantBloc?.(item.id)}
                  className="gap-2"
                >
                  <item.icon className="h-4 w-4" style={{ color: item.color }} />
                  {item.label}
                </Button>
              ))
              : navItems.map((item) => (
                <Button
                  key={item.id}
                  data-tour={`nav-${item.id}`}
                  variant={currentPage === item.id ? "secondary" : "ghost"}
                  onClick={() => onNavigate(item.id)}
                  className="gap-2"
                >
                  <item.icon className="h-4 w-4" style={{ color: item.color }} />
                  {item.label}
                </Button>
              ))}
          </nav>

          {/* Right side: streak + bell + lang + avatar + name + logout */}
          <div className="flex shrink-0 items-center gap-3">
            {/* Switcher « Deux casquettes » — contrôle SEGMENTÉ Play ↔ Pilot, les deux
                labels toujours visibles, segment actif en couleur forte (bien repérable). */}
            {encadrantRole && roleMeta && (
              <div
                data-tour="view-switch"
                className="hidden lg:flex shrink-0 items-center rounded-full p-1 gap-1"
                aria-label={t("nav.viewSwitch")}
                style={{ background: "#EEF1F7", boxShadow: `inset 0 0 0 1.5px ${roleMeta.color}` }}
              >
                <button
                  onClick={() => { if (inEncadrant) onNavigate("dashboard"); }}
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-bold transition"
                  style={!inEncadrant ? { background: "#1E7A3C", color: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,.2)" } : { color: "#5A6076" }}
                >
                  <Play className="h-3.5 w-3.5" fill={!inEncadrant ? "#fff" : "none"} />{t("nav.viewPlay")}
                </button>
                <button
                  onClick={() => { if (!inEncadrant) onNavigate("encadrant"); }}
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-bold transition"
                  style={inEncadrant ? { background: roleMeta.color, color: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,.2)" } : { color: "#5A6076" }}
                >
                  <Settings2 className="h-3.5 w-3.5" />{t("nav.viewSetup")}
                </button>
              </div>
            )}
            {/* Guide didacticiel — bouton + menu « trouver une fonctionnalité » */}
            <div className="hidden lg:block relative">
              <button
                onClick={() => setHelpMenuOpen((v) => !v)}
                className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-muted transition-colors"
                title={t("guidedTour.menu.title")}
                aria-label={t("guidedTour.menu.title")}
                aria-haspopup="menu"
                aria-expanded={helpMenuOpen}
              >
                <HelpCircle className="h-5 w-5" style={{ color: "#384872" }} />
              </button>
              {helpMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setHelpMenuOpen(false)} aria-hidden="true" />
                  <div role="menu" className="absolute right-0 mt-2 w-64 z-50 rounded-lg border bg-white shadow-lg py-1">
                    <button
                      role="menuitem"
                      onClick={() => { try { localStorage.setItem("bizcoach_help_scope", inEncadrant ? "pilot" : "play"); } catch { /* noop */ } setHelpMenuOpen(false); onNavigate("help"); }}
                      className="flex w-full items-center gap-2 text-left px-3 py-2 text-sm font-medium hover:bg-muted transition-colors"
                      style={{ color: "#384872" }}
                    >
                      <BookOpen className="h-4 w-4 shrink-0" />
                      {t("clientGuide.menuEntry")}
                    </button>
                    {legalDocs?.privacy && (
                      // Politique de confidentialité : mise à disposition pour information (13/10/2026).
                      <a
                        role="menuitem"
                        href={publicLegalPdfUrl(legalDocs.privacy.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setHelpMenuOpen(false)}
                        className="flex w-full items-center gap-2 text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                        style={{ color: "#384872" }}
                      >
                        <ShieldCheck className="h-4 w-4 shrink-0" />
                        {t("legalLinks.privacyPolicy")}
                      </a>
                    )}
                    <div className="my-1 h-px bg-muted" />
                    <p className="px-3 py-2 text-xs font-semibold text-muted-foreground">{t("guide.hub.title")}</p>
                    {walkthroughItems.map((w) => (
                      <button
                        key={w.id}
                        role="menuitem"
                        onClick={() => launchWalkthrough(w.id)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                      >
                        {t(w.titleKey)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Compteur BizCoins (permanent, façon Duolingo) — visible mobile + desktop */}
            <BizCoinsBadge onNavigate={(page) => onNavigate(page as PageType)} />

            {/* Streak (flamme) — caché sur mobile */}
            <div className="hidden lg:block">
              <StreakBadge />
            </div>

            {/* Cloche notifications */}
            <NotificationBell onNavigate={() => onNavigate("notifications")} />

            {/* Language toggle — flag */}
            <button
              onClick={handleLanguageToggle}
              className="hidden sm:flex items-center justify-center h-8 w-8 rounded-full hover:bg-muted transition-colors"
              title={i18n.language === "fr" ? "Switch to English" : "Passer en français"}
              aria-label={i18n.language === "fr" ? "Switch to English" : "Passer en français"}
            >
              <span className="text-lg leading-none">{i18n.language === "fr" ? "🇫🇷" : "🇬🇧"}</span>
            </button>

            {/* Compteur sessions (rate limit — masqué pour Kestio interne et staging/dev) */}
            <div className="hidden lg:flex">
              <SessionQuotaIndicator
                onClick={() => onNavigate("account")}
                onBuyPack={canBuyLicences(user.profileType) ? () => ouvrirAchatDePack(onNavigate) : undefined}
              />
            </div>

            {/* Menu profil (desktop) — clic sur la photo → Mon profil / Paramètres / Admin / Déconnexion */}
            <div className="hidden lg:block">
              <DropdownMenu>
                <DropdownMenuTrigger data-tour="profile-menu" className="flex items-center gap-1.5 rounded-full p-0.5 pr-1.5 hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={user.firstName}>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.profilePicture} />
                    <AvatarFallback>
                      {user.firstName[0]}
                      {user.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{user.firstName}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-semibold leading-tight" style={{ color: "#384872" }}>{user.firstName} {user.lastName}</p>
                    <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {profileMenuItems.map((item) => (
                    <DropdownMenuItem key={item.id} onSelect={() => onNavigate(item.id)} className="cursor-pointer gap-2">
                      <item.icon className="h-4 w-4 text-muted-foreground" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => onLogout()} className="cursor-pointer gap-2 text-red-600 focus:text-red-700">
                    <LogOut className="h-4 w-4" />
                    {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile : l'avatar seul. Le prénom n'apprend rien à qui est
                connecté, et la barre du haut est l'espace le plus disputé de
                l'écran — chaque élément doit gagner sa place. */}
            <div className="flex lg:hidden items-center gap-2">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user.profilePicture} />
                <AvatarFallback>
                  {user.firstName[0]}
                  {user.lastName[0]}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:inline text-sm font-medium">{user.firstName}</span>
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t bg-background">
            <nav className="container mx-auto px-6 py-4 space-y-2">
              {/* Toggle « Deux casquettes » (mobile) */}
              {encadrantRole && roleMeta && (
                <div className="flex items-center rounded-full border p-0.5 mb-2" style={{ borderColor: "var(--border)" }}>
                  <button
                    onClick={() => { if (inEncadrant) { onNavigate("dashboard"); setMobileMenuOpen(false); } }}
                    className="flex-1 rounded-full px-3 py-1.5 text-sm font-semibold inline-flex items-center justify-center gap-1.5"
                    style={!inEncadrant ? { background: "#384872", color: "#fff" } : { color: "#6A7189" }}
                  ><Play className="h-3.5 w-3.5" />{t("nav.viewPlay")}</button>
                  <button
                    onClick={() => { if (!inEncadrant) { onNavigate("encadrant"); setMobileMenuOpen(false); } }}
                    className="flex-1 rounded-full px-3 py-1.5 text-sm font-semibold inline-flex items-center justify-center gap-1.5"
                    style={inEncadrant ? { background: roleMeta.color, color: "#fff" } : { color: "#6A7189" }}
                  ><Settings2 className="h-3.5 w-3.5" />{t("nav.viewSetup")}</button>
                </div>
              )}
              {(inEncadrant ? blocItems : navItems).map((item) => (
                <Button
                  key={item.id}
                  /**
                   * Même ancre que la barre de navigation, sur la version repliée.
                   *
                   * `bloc-nav-*` n'existait que dans la barre, en `hidden lg:flex` :
                   * sous 1024 px, les visites guidées de l'espace encadrant ne
                   * trouvaient donc plus rien à révéler et s'arrêtaient. Les deux
                   * exemplaires coexistent maintenant dans le DOM ; `waitForElement`
                   * (InteractiveGuide) retient celui qui est réellement visible.
                   */
                  {...(inEncadrant ? { "data-tour": `bloc-nav-${item.id}` } : {})}
                  variant={(inEncadrant ? encadrantBloc === item.id : currentPage === item.id) ? "secondary" : "ghost"}
                  onClick={() => {
                    if (inEncadrant) { onSetEncadrantBloc?.(item.id as EncadrantBloc); }
                    else { onNavigate(item.id as PageType); }
                    setMobileMenuOpen(false);
                  }}
                  className="w-full justify-start gap-2"
                >
                  <item.icon className="h-4 w-4" style={{ color: item.color }} />
                  {item.label}
                </Button>
              ))}
              <div className="my-2 h-px bg-muted" />
              {/* Profil (mobile) — Mon profil / Paramètres / Admin */}
              {profileMenuItems.map((item) => (
                <Button
                  key={item.id}
                  variant={currentPage === item.id ? "secondary" : "ghost"}
                  onClick={() => { onNavigate(item.id); setMobileMenuOpen(false); }}
                  className="w-full justify-start gap-2"
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Button>
              ))}
              <div className="my-2 h-px bg-muted" />
              <Button
                variant="ghost"
                onClick={handleLanguageToggle}
                className="w-full justify-start gap-2"
              >
                <span className="text-base leading-none">{i18n.language === "fr" ? "🇫🇷" : "🇬🇧"}</span>
                {i18n.language === "fr" ? "English" : "Français"}
              </Button>
              {/* Guide d'utilisation (mobile) */}
              <Button
                variant="ghost"
                onClick={() => { try { localStorage.setItem("bizcoach_help_scope", inEncadrant ? "pilot" : "play"); } catch { /* noop */ } onNavigate("help"); setMobileMenuOpen(false); }}
                className="w-full justify-start gap-2"
              >
                <BookOpen className="h-4 w-4" style={{ color: "#384872" }} />
                {t("clientGuide.menuEntry")}
              </Button>
              {/* Politique de confidentialité (mobile) : mise à disposition pour information (13/10/2026). */}
              {legalDocs?.privacy && (
                <Button variant="ghost" asChild className="w-full justify-start gap-2">
                  <a href={publicLegalPdfUrl(legalDocs.privacy.id)} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                    <ShieldCheck className="h-4 w-4" style={{ color: "#384872" }} />
                    {t("legalLinks.privacyPolicy")}
                  </a>
                </Button>
              )}
              {/* Visite guidée (mobile) — découverte de l'accueil */}
              <Button
                variant="ghost"
                onClick={() => launchWalkthrough("discover-home")}
                className="w-full justify-start gap-2"
              >
                <HelpCircle className="h-4 w-4" style={{ color: "#384872" }} />
                {t("guide.hub.title")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  onLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4" />
                {t("nav.logout")}
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Banner — active training session (below header, right-aligned) */}
      {activeTraining && currentPage !== "training" && (
        <div className="sticky top-16 z-40 flex justify-end px-6 py-2">
          <button
            onClick={() => {
              setMobileMenuOpen(false);
              activeTraining.onReturn();
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-full shadow-md text-white text-sm font-medium transition-all hover:scale-105 hover:shadow-lg animate-pulse"
            style={{ backgroundColor: activeTraining.agentColor || "#384872" }}
          >
            {activeTraining.agentImage ? (
              <img src={activeTraining.agentImage} alt="" loading="lazy" className="h-5 w-5 rounded-full object-cover" style={{ objectPosition: 'top' }} />
            ) : activeTraining.agentIcon ? (
              <span>{activeTraining.agentIcon}</span>
            ) : (
              <Phone className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">{t("training.sessionInProgress")}</span>
            <span className="sm:hidden">{t("training.sessionInProgress")}</span>
          </button>
        </div>
      )}

      {/* Banner — active challenges */}
      {!challengeBannerDismissed && activeChallenges && activeChallenges.length > 0 && currentPage === "dashboard" && !activeTraining && (
        <div className="sticky top-16 z-40 overflow-hidden relative" style={{
          background: "linear-gradient(135deg, #384872 0%, #5b3e8a 40%, #f6c847 100%)",
          backgroundSize: "200% 200%",
          animation: "gradientMove 8s ease infinite",
        }}>
          {/* Animated particles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-white"
                style={{
                  width: 4 + (i % 3) * 2,
                  height: 4 + (i % 3) * 2,
                  left: `${10 + i * 15}%`,
                  opacity: 0.15 + (i % 3) * 0.1,
                  animation: `float ${3 + i * 0.7}s ease-in-out ${i * 0.4}s infinite`,
                }}
              />
            ))}
          </div>
          {/* Shimmer sweep */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.25) 50%, transparent 60%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 4s ease-in-out infinite",
            }}
          />
          <style>{`
            @keyframes gradientMove { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
            @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
            @keyframes slideUp { 0% { opacity: 0; transform: translateY(12px); } 100% { opacity: 1; transform: translateY(0); } }
            @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-18px); } }
            @keyframes bounce-trophy { 0%,100% { transform: translateY(0) rotate(0deg); } 25% { transform: translateY(-3px) rotate(-5deg); } 75% { transform: translateY(-3px) rotate(5deg); } }
            @keyframes glow-pulse { 0%,100% { box-shadow: 0 0 10px rgba(246,200,71,0.3), 0 0 20px rgba(246,200,71,0.1); } 50% { box-shadow: 0 0 20px rgba(246,200,71,0.6), 0 0 40px rgba(246,200,71,0.2); } }
            @keyframes join-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
          `}</style>
          <div className="container mx-auto px-6 py-3.5 relative z-10">
            <div className="flex items-center gap-3">
              {/* Trophy — animated bounce */}
              <div className="shrink-0" style={{ animation: "glow-pulse 2.5s ease-in-out infinite" }}>
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                  <Trophy className="h-5 w-5 text-yellow-300" style={{ animation: "bounce-trophy 2s ease-in-out infinite" }} />
                </div>
              </div>
              {/* Title */}
              <div className="shrink-0 hidden sm:block" style={{ animation: "slideUp 0.5s ease-out both" }}>
                <p className="text-white font-bold text-sm tracking-wide uppercase">{t("challenge.activeChallengesBanner")}</p>
                <p className="text-white/50 text-xs">{activeChallenges.length} challenge{activeChallenges.length > 1 ? "s" : ""}</p>
              </div>
              {/* Challenge cards */}
              <div className="flex items-center gap-3 flex-1 overflow-x-auto px-1 py-1 scrollbar-none">
                {activeChallenges.map((ch, idx) => {
                  const daysLeft = ch.endDate ? Math.max(0, Math.ceil((new Date(ch.endDate).getTime() - Date.now()) / 86400000)) : null;
                  const isEnrolled = ch.enrolled;
                  // En pause : visible avec la mention « En pause », sans inscription (point 1, 30/09/2026).
                  const isPaused = (ch as { status?: string }).status === "paused";
                  return (
                    <div
                      key={ch.id}
                      className="shrink-0 rounded-xl overflow-hidden"
                      style={{ animation: `slideUp 0.5s ease-out ${0.15 + idx * 0.12}s both` }}
                    >
                      <div
                        className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl hover:bg-white/25 transition-all hover:scale-[1.04] hover:shadow-xl active:scale-[0.97]"
                        style={{ minWidth: 200 }}
                      >
                        <button
                          onClick={() => {
                            if (isEnrolled || ch.challengeType === "mandatory" || isPaused) { onNavigateToChallenge?.(ch.id); return; }
                            // Inter challenges on desktop → full brief page
                            if (ch.scope && ch.scope !== "intra" && window.innerWidth >= 768) {
                              setInterBriefChallengeId(ch.id);
                            } else {
                              setJoinConfirmChallenge(ch);
                            }
                          }}
                          className="flex items-center gap-3 px-4 py-2.5 w-full text-left"
                        >
                          {/* Simulator icon */}
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-md" style={{ backgroundColor: ch.simulatorColor || "#384872" }}>
                            {ch.simulatorIcon && ch.simulatorIcon.startsWith("data:") ? (
                              <img src={ch.simulatorIcon} alt="" loading="lazy" className="h-5 w-5 object-contain" />
                            ) : ch.simulatorIcon ? (
                              <span className="text-sm">{ch.simulatorIcon}</span>
                            ) : (
                              <Zap className="h-4 w-4 text-white" />
                            )}
                          </div>
                          {/* Info */}
                          <div className="text-left min-w-0 flex-1">
                            <p className="text-white font-semibold text-sm truncate max-w-[150px]">{ch.title}</p>
                            {daysLeft !== null && (
                              <span className="text-white/60 text-xs flex items-center gap-1">
                                <Flame className="h-3 w-3 text-orange-300" style={{ animation: "bounce-trophy 1.5s ease-in-out infinite" }} />
                                {daysLeft > 0 ? t("challenge.timeRemaining", { days: daysLeft }) : t("challenge.timeRemainingHours", { hours: Math.max(1, Math.ceil((new Date(ch.endDate!).getTime() - Date.now()) / 3600000)) })}
                              </span>
                            )}
                          </div>
                          {/* Action indicator */}
                          {isPaused ? (
                            <span className="shrink-0 px-3 py-1 rounded-full bg-white/25 text-white text-xs font-bold" title={t("challenge.pausedNoPlay")}>{t("challenge.statusPaused")}</span>
                          ) : (isEnrolled || ch.challengeType === "mandatory" || !canJoinChallenges) ? (
                            <ChevronRight className="h-5 w-5 text-white/50 shrink-0" />
                          ) : (
                            <span className="hidden sm:inline-flex shrink-0 px-3.5 py-1.5 rounded-full bg-yellow-400 text-gray-900 text-xs font-extrabold shadow-lg" style={{ animation: "join-pulse 2s ease-in-out infinite" }}>
                              {t("challenge.joinChallenge")}
                            </span>
                          )}
                        </button>
                        {/* Mobile: "Participer" on separate line — only for optional challenges */}
                        {!isEnrolled && ch.challengeType !== "mandatory" && canJoinChallenges && !isPaused && (
                          <div className="sm:hidden px-4 pb-2.5 -mt-1">
                            <button
                              onClick={() => setJoinConfirmChallenge(ch)}
                              // 29 px sans hauteur minimale — c'est pourtant LE
                              // bouton du bandeau, et le seul chemin d'inscription
                              // sur mobile.
                              className="w-full min-h-[32px] px-3.5 py-1.5 rounded-full bg-yellow-400 text-gray-900 text-xs font-extrabold shadow-lg text-center"
                              style={{ animation: "join-pulse 2s ease-in-out infinite" }}
                            >
                              {t("challenge.joinChallenge")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Dismiss */}
              <button
                onClick={() => setChalleneBannerDismissed(true)}
                /**
                 * `aria-label` : le bouton n'avait aucun texte — ni visible, ni
                 * accessible. Un lecteur d'écran annonçait « bouton », sans plus,
                 * et le test des cibles tactiles ne pouvait le désigner que par
                 * « ? », ce qui rendait le diagnostic impossible.
                 * `min-h/min-w` : 24 px, sous le seuil de confort tactile — et
                 * c'est le geste « faire disparaître », qu'on ne veut pas rater.
                 */
                aria-label={t("common.close")}
                className="text-white/30 hover:text-white/70 p-1 shrink-0 transition-colors inline-flex min-h-[32px] min-w-[32px] items-center justify-center"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join confirmation modal */}
      {/* Brief plein écran pour challenges inter (desktop) */}
      {interBriefChallengeId && (
        <div className="fixed inset-0 z-[60] bg-white overflow-y-auto">
          <ChallengeBriefPage
            challengeId={interBriefChallengeId}
            onBack={() => setInterBriefChallengeId(null)}
            onJoin={!canJoinChallenges ? undefined : async () => {
              // Check if user has a pseudo for inter challenges
              if (!user.pseudo) {
                setPendingJoinChallengeId(interBriefChallengeId);
                setPseudoModalOpen(true);
                return;
              }
              try {
                setJoiningId(interBriefChallengeId);
                await challengesApi.joinChallenge(interBriefChallengeId);
                const joinedId = interBriefChallengeId;
                setInterBriefChallengeId(null);
                onChallengeJoined?.();
                onNavigateToChallenge?.(joinedId);
              } catch (err: any) {
                alert(err.message);
              } finally {
                setJoiningId(null);
              }
            }}
          />
        </div>
      )}

      {/* PseudoModal pour inscription inter */}
      <PseudoModal
        open={pseudoModalOpen}
        onClose={() => { setPseudoModalOpen(false); setPendingJoinChallengeId(null); }}
        onSaved={async (pseudo, avatarSeed, avatarUrl) => {
          setPseudoModalOpen(false);
          // Pseudo saved — user will be refreshed on next auth/me call
          // Now join the challenge
          if (pendingJoinChallengeId) {
            try {
              setJoiningId(pendingJoinChallengeId);
              await challengesApi.joinChallenge(pendingJoinChallengeId);
              setInterBriefChallengeId(null);
              setJoinConfirmChallenge(null);
              onNavigateToChallenge?.(pendingJoinChallengeId);
            } catch (err: any) {
              alert(err.message);
            } finally {
              setJoiningId(null);
              setPendingJoinChallengeId(null);
            }
          }
        }}
        currentPseudo={(user as any).pseudo}
        currentAvatarSeed={(user as any).avatarSeed}
      />

      {joinConfirmChallenge && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" role="dialog" aria-modal="true" onClick={() => !joiningId && setJoinConfirmChallenge(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header with simulator color */}
            <div className="p-6 pb-4" style={{ background: `linear-gradient(135deg, ${joinConfirmChallenge.simulatorColor || "#384872"} 0%, #384872 100%)` }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  {joinConfirmChallenge.simulatorIcon?.startsWith("data:") ? (
                    <img src={joinConfirmChallenge.simulatorIcon} alt="" loading="lazy" className="h-7 w-7 object-contain" />
                  ) : joinConfirmChallenge.simulatorIcon ? (
                    <span className="text-xl">{joinConfirmChallenge.simulatorIcon}</span>
                  ) : (
                    <Trophy className="h-6 w-6 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">{joinConfirmChallenge.title}</h3>
                  <p className="text-white/70 text-sm">{joinConfirmChallenge.simulatorName}</p>
                </div>
              </div>
            </div>
            {/* Body */}
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {joinConfirmChallenge.description && (
                <div className="animate-[fadeIn_0.3s_ease-out]">
                  <p className="text-gray-600 text-sm leading-relaxed">{joinConfirmChallenge.description}</p>
                </div>
              )}

              {/* === BRIEF INTER === */}
              {joinChallengeDetails?.scope && joinChallengeDetails.scope !== "intra" && (
                <div className="space-y-3 animate-[fadeIn_0.35s_ease-out]">
                  {joinChallengeDetails.fictiveCompanyName && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="h-4 w-4 text-[#384872]" />
                        <span className="text-xs font-semibold text-[#384872] uppercase tracking-wide">{i18n.language === "fr" ? "L'entreprise" : "The company"}</span>
                      </div>
                      <p className="font-semibold text-sm" style={{ color: "#384872" }}>{joinChallengeDetails.fictiveCompanyName}</p>
                      {joinChallengeDetails.fictiveCompanyDescription && (
                        <p className="text-xs text-muted-foreground mt-1">{joinChallengeDetails.fictiveCompanyDescription}</p>
                      )}
                    </div>
                  )}
                  {joinChallengeDetails.offers?.length > 0 && (
                    <div className="p-3 rounded-xl bg-green-50/50 border border-green-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="h-4 w-4 text-[#7CBF94]" />
                        <span className="text-xs font-semibold text-[#1E7A3C] uppercase tracking-wide">
                          {i18n.language === "fr" ? `${joinChallengeDetails.offers.length} offre(s) à présenter` : `${joinChallengeDetails.offers.length} offer(s)`}
                        </span>
                      </div>
                      {joinChallengeDetails.offers.map((o: any, idx: number) => (
                        <div key={idx} className="mt-1">
                          <span className="text-sm font-medium" style={{ color: "#384872" }}>{o.name}</span>
                          {o.description && <span className="text-xs text-muted-foreground ml-1">— {o.description}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {joinChallengeDetails.personae?.length > 0 && (
                    <div className="p-3 rounded-xl bg-purple-50/50 border border-purple-100">
                      <div className="flex items-center gap-2 mb-1">
                        <UserCircle className="h-4 w-4 text-[#988EF2]" />
                        <span className="text-xs font-semibold text-[#5A4BBE] uppercase tracking-wide">
                          {i18n.language === "fr" ? `${joinChallengeDetails.personae.length} profil(s) client(s)` : `${joinChallengeDetails.personae.length} profile(s)`}
                        </span>
                      </div>
                      {joinChallengeDetails.personae.map((p: any, idx: number) => (
                        <div key={idx} className="mt-1 text-sm">
                          <span className="font-medium" style={{ color: "#384872" }}>{p.function || p.csp || "Profil"}</span>
                          <span className="text-xs text-muted-foreground ml-1">({p.personaType} — {p.category === "customer_care" ? "SAV" : "Sales"})</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(joinChallengeDetails.badge || joinChallengeDetails.giftName) && (
                    <div className="p-3 rounded-xl bg-yellow-50/50 border border-yellow-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Star className="h-4 w-4 text-[#F6C847]" />
                        <span className="text-xs font-semibold text-[#926B00] uppercase tracking-wide">{i18n.language === "fr" ? "À gagner" : "Rewards"}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        {joinChallengeDetails.badge && (
                          <div className="flex items-center gap-2">
                            <img src={joinChallengeDetails.badge.imageUrl} alt="" className="w-8 h-8 rounded" />
                            <span className="text-sm font-medium">{joinChallengeDetails.badge.name}</span>
                          </div>
                        )}
                        {joinChallengeDetails.giftName && (
                          <div className="flex items-center gap-2">
                            {joinChallengeDetails.giftPhotoUrl && <img src={joinChallengeDetails.giftPhotoUrl} alt="" className="w-8 h-8 rounded" />}
                            <span className="text-sm font-medium">{joinChallengeDetails.giftName}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3 animate-[fadeIn_0.4s_ease-out]">
                {/* Objective 1 */}
                {joinConfirmChallenge.primaryObjective && (() => {
                  const objLabels: Record<string, string> = {
                    most_rdv: t("challenge.objectiveMostRdv"),
                    most_needs: t("challenge.objectiveMostNeeds"),
                    deal_closed: t("challenge.objectiveDealClosed"),
                    pitch_validated: t("challenge.objectivePitchValidated"),
                    tension_mastered: t("challenge.objectiveTensionMastered"),
                    clear_outcome: t("challenge.objectiveClearOutcome"),
                    cumulative_score: t("challenge.objectiveCumulativeScore"),
                    best_average: t("challenge.objectiveBestAverage"),
                  };
                  const resolveLabel = (key: string) => {
                    if (objLabels[key]) return objLabels[key];
                    const criterion = (joinConfirmChallenge.simulatorScoringCriteria as any[] || []).find((c: any) => c.key === key);
                    if (criterion) return pickScoringLabel(criterion, i18n.language);
                    return key;
                  };
                  return (
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-100">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Target className="h-4 w-4 text-amber-600" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide">{t("challenge.joinObjective1")}</div>
                        <div className="text-sm font-medium text-gray-800">{resolveLabel(joinConfirmChallenge.primaryObjective)}</div>
                      </div>
                    </div>
                  );
                })()}
                {/* Objective 2 */}
                {joinConfirmChallenge.secondaryObjective && (() => {
                  const objLabels: Record<string, string> = {
                    most_rdv: t("challenge.objectiveMostRdv"),
                    most_needs: t("challenge.objectiveMostNeeds"),
                    deal_closed: t("challenge.objectiveDealClosed"),
                    pitch_validated: t("challenge.objectivePitchValidated"),
                    tension_mastered: t("challenge.objectiveTensionMastered"),
                    clear_outcome: t("challenge.objectiveClearOutcome"),
                    cumulative_score: t("challenge.objectiveCumulativeScore"),
                    best_average: t("challenge.objectiveBestAverage"),
                  };
                  const resolveLabel = (key: string) => {
                    if (objLabels[key]) return objLabels[key];
                    const criterion = (joinConfirmChallenge.simulatorScoringCriteria as any[] || []).find((c: any) => c.key === key);
                    if (criterion) return pickScoringLabel(criterion, i18n.language);
                    return key;
                  };
                  return (
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Crosshair className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-purple-600 uppercase tracking-wide">{t("challenge.joinObjective2")}</div>
                        <div className="text-sm font-medium text-gray-800">{resolveLabel(joinConfirmChallenge.secondaryObjective)}</div>
                      </div>
                    </div>
                  );
                })()}
                {/* Sessions count */}
                {joinConfirmChallenge.maxSessions && (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <Zap className="h-4 w-4 text-gray-600" />
                    </div>
                    <span className="text-sm text-gray-700">{t("challenge.joinSessionsCount", { count: joinConfirmChallenge.maxSessions })}</span>
                  </div>
                )}
                {/* Period */}
                {(joinConfirmChallenge.startDate || joinConfirmChallenge.endDate) && (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <Calendar className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t("challenge.joinPeriod")}</div>
                      <div className="text-sm text-gray-700">
                        {t("challenge.joinPeriodValue", {
                          start: joinConfirmChallenge.startDate ? new Date(joinConfirmChallenge.startDate).toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—",
                          end: joinConfirmChallenge.endDate ? new Date(joinConfirmChallenge.endDate).toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—",
                        })}
                      </div>
                    </div>
                  </div>
                )}
                {/* Time left to register */}
                {joinConfirmChallenge.endDate && (() => {
                  const msLeft = new Date(joinConfirmChallenge.endDate).getTime() - Date.now();
                  const days = Math.max(0, Math.ceil(msLeft / 86400000));
                  const hours = Math.max(1, Math.ceil(msLeft / 3600000));
                  if (msLeft <= 0) return null;
                  return (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-orange-50 to-red-50 border border-orange-100 animate-[pulse_3s_ease-in-out_infinite]">
                      <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                        <Clock className="h-4 w-4 text-orange-600" />
                      </div>
                      <span className="text-sm font-medium text-orange-700">
                        {days > 0 ? t("challenge.joinTimeLeft", { days }) : t("challenge.joinTimeLeftHours", { hours })}
                      </span>
                    </div>
                  );
                })()}
              </div>
              {/* Team selection for participant_choice challenges */}
              {joinConfirmChallenge.teamAssignmentMode === "participant_choice" && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">{t("challenge.chooseTeam")}</h4>
                  {loadingTeams ? (
                    <p className="text-sm text-gray-400 italic">{t("challenge.loadingTeams")}</p>
                  ) : (
                    <div className="space-y-2">
                      {joinTeams.map(team => (
                        <button
                          key={team.id}
                          onClick={() => setJoinSelectedTeamId(team.id)}
                          className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                            joinSelectedTeamId === team.id
                              ? "border-yellow-400 bg-yellow-50 shadow-sm"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded-full shrink-0"
                              style={{ backgroundColor: team.color || "#384872" }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900">{team.name}</div>
                              <div className="text-xs text-gray-500">
                                {t("challenge.teamMembers", { count: team.members.length })}
                                {team.members.length > 0 && (
                                  <span className="ml-1 text-gray-400">
                                    — {team.members.slice(0, 3).map(m => m.firstName).join(", ")}
                                    {team.members.length > 3 && "..."}
                                  </span>
                                )}
                              </div>
                            </div>
                            {joinSelectedTeamId === team.id && (
                              <div className="w-5 h-5 rounded-full bg-yellow-400 flex items-center justify-center shrink-0">
                                <svg className="w-3 h-3 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                      {!loadingTeams && joinTeams.length === 0 && (
                        <p className="text-sm text-gray-400 italic">{t("challenge.noTeams")}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setJoinConfirmChallenge(null)}
                  disabled={!!joiningId}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={async () => {
                    const challengeToJoin = joinConfirmChallenge;
                    const hasTeamChoice = challengeToJoin.teamAssignmentMode === "participant_choice" && joinTeams.length > 0;
                    if (hasTeamChoice && !joinSelectedTeamId) return; // guard: must select team
                    setJoiningId(challengeToJoin.id);
                    try {
                      await challengesApi.joinChallenge(challengeToJoin.id, joinSelectedTeamId || undefined);
                      onChallengeJoined?.();
                      setJoinConfirmChallenge(null);
                      // Navigate to challenge page after joining
                      onNavigateToChallenge?.(challengeToJoin.id);
                    } catch (err: any) {
                      alert(err.message);
                    } finally {
                      setJoiningId(null);
                    }
                  }}
                  disabled={!!joiningId || (joinConfirmChallenge.teamAssignmentMode === "participant_choice" && joinTeams.length > 0 && !joinSelectedTeamId)}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors shadow-md ${
                    joinConfirmChallenge.teamAssignmentMode === "participant_choice" && joinTeams.length > 0 && !joinSelectedTeamId
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-yellow-400 text-gray-900 hover:bg-yellow-300"
                  }`}
                >
                  {joiningId ? t("challenge.joining") : t("challenge.joinConfirmBtn")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-6 py-3 lg:py-6">
        <GuideContext.Provider value={{ runWalkthrough }}>{children}</GuideContext.Provider>
      </main>

      {/* Chat support SalesIQ */}
      <SupportChat />
    </div>
  );
}
